import * as React from 'react'
import { DateTime } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlay } from '@fortawesome/free-solid-svg-icons/faPlay'
import { faPause } from '@fortawesome/free-solid-svg-icons/faPause'
import { faRotateLeft } from '@fortawesome/free-solid-svg-icons/faRotateLeft'
import { useSnapshot } from 'valtio'
import { dayHistoryState } from '../../../../store/dayHistoryState.ts'
import { authState, effectiveTimezone } from '../../../../store/authState.ts'
import { cycleSpeed, formatSpeed } from '../../../../helpers/dayHistoryHelper.ts'

/**
 * Day History playback controls: play/pause, speed toggle, clock, gap indicator.
 * Positioned as an overlay at the bottom of the map.
 */
export const DayHistoryControls: React.FC = () => {
    const isPlaying = (useSnapshot(dayHistoryState) as typeof dayHistoryState).isPlaying
    const playbackSpeed = (useSnapshot(dayHistoryState) as typeof dayHistoryState).playbackSpeed
    const currentTime = (useSnapshot(dayHistoryState) as typeof dayHistoryState).currentTime
    const pathData = (useSnapshot(dayHistoryState) as typeof dayHistoryState).pathData
    const loading = (useSnapshot(dayHistoryState) as typeof dayHistoryState).loading
    const error = (useSnapshot(dayHistoryState) as typeof dayHistoryState).error
    useSnapshot(authState)
    const zone = effectiveTimezone().value

    if (!pathData && !loading && !error) return null

    const togglePlay = () => {
        const store = dayHistoryState
        if (!store.pathData) return

        // If at end, restart from beginning
        if (store.currentTime >= store.pathData.duration.end) {
            store.currentTime = store.pathData.duration.start
        }
        store.isPlaying = !store.isPlaying
    }

    const restart = () => {
        const store = dayHistoryState
        if (!store.pathData) return
        store.isPlaying = false
        store.currentTime = store.pathData.duration.start
    }

    const changeSpeed = () => {
        const next = cycleSpeed(playbackSpeed)
        dayHistoryState.playbackSpeed = next
    }

    // Format current simulated time in user's timezone
    const clockDisplay = currentTime
        ? DateTime.fromSeconds(currentTime).setZone(zone).toFormat('h:mm:ss a')
        : '--:--'

    // No data state
    if (pathData && pathData.path.length === 0) {
        return (
            <div className={'bg-base-100 rounded-xl border-2 border-primary/20 p-3 shadow-lg text-center'}>
                <div className={'text-primary font-semibold'}>No activity recorded for this day</div>
            </div>
        )
    }

    // Loading state
    if (loading) {
        return (
            <div className={'bg-base-100 rounded-xl border-2 border-primary/20 p-3 shadow-lg flex items-center justify-center gap-2'}>
                <span className="loading loading-spinner loading-sm text-primary"></span>
                <span className={'text-primary font-semibold'}>Loading path...</span>
            </div>
        )
    }

    // Error state
    if (error) {
        return (
            <div className={'bg-base-100 rounded-xl border-2 border-primary/20 p-3 shadow-lg text-center'}>
                <div className={'text-error font-semibold'}>{error}</div>
            </div>
        )
    }

    return (
        <div className={'bg-base-100 rounded-xl border-2 border-primary/20 p-3 shadow-lg flex items-center gap-3 w-fit mx-auto -mt-4'}>
            {/* Restart button */}
            <button
                className={'btn btn-ghost btn-sm btn-circle text-primary'}
                onClick={restart}
                title={'Restart'}
            >
                <FontAwesomeIcon icon={faRotateLeft} />
            </button>

            {/* Play/Pause */}
            <button
                className={'btn btn-primary btn-sm btn-circle'}
                onClick={togglePlay}
                title={isPlaying ? 'Pause' : 'Play'}
            >
                <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
            </button>

            {/* Speed toggle */}
            <button
                className={'btn btn-ghost btn-xs text-primary font-bold min-w-[4.5rem]'}
                onClick={changeSpeed}
                title={'Change playback speed'}
            >
                {formatSpeed(playbackSpeed)}
            </button>

            {/* Clock */}
            <div className={'text-primary font-mono text-sm font-semibold min-w-[5.5rem] text-center'}>
                {clockDisplay}
            </div>
        </div>
    )
}
